import { inject, Injectable } from '@angular/core';
import { ResultUpdate, StoreDataTypeKey } from '@etoh/database/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { map, Observable, of, switchMap, take, tap } from 'rxjs';
import { RxjsOperatorsService } from '../../rxjs-operators.service';

import { storeConfigurationBulkConfigState } from '../store.interface';
import { StoreService } from '../store.service';
import { getEntitiesWording } from './entities.wordings';

import { sortBy } from 'lodash';
import * as stringSimilarity from 'string-similarity';
import { formatPercent } from '../../locales/locales';
import { ListAction } from '../shared/interfaces/listOptions.interface';

@Injectable({
  providedIn: 'root',
})
export class EntitiesService {
  private modal = inject(NzModalService);
  private message = inject(NzMessageService);
  private storeService = inject(StoreService);
  private rxjsOperatorsService = inject(RxjsOperatorsService);

  public getEntityAction<T>(dataType: StoreDataTypeKey): ListAction<any>[] {
    return [
      {
        name: 'Archive',
        isDisabled: (datas: any[]) => {
          return datas.some((data) => data.archivedAt);
        },
        action: (datas: any[]) => {
          this.archivate(
            dataType,
            datas.map((d) => d.id)
          ).subscribe((res) => {
            if (res) {
              // this.clearSelection();
            }
          });
        },
      },
      {
        name: 'Unarchive',
        roles: ['user'],
        isDisabled: (datas: any[]) => {
          return datas.some((data) => !data.archivedAt);
        },
        action: (datas: any[]) => {
          this.unarchive(
            dataType,
            datas.map((d) => d.id)
          ).subscribe((res) => {
            if (res) {
              // this.clearSelection();
            }
          });
        },
      },
      {
        name: 'Delete',
        roles: ['user'],
        isDisabled: (datas: any[]) => {
          return datas.some((data) => data.deletedAt);
        },
        action: (datas: any[]) => {
          this.delete(
            dataType,
            datas.map((d) => d.id)
          ).subscribe((res) => {
            if (res) {
              // this.clearSelection();
            }
          });
        },
      },
    ];
  }

  public archivate(
    dataType: StoreDataTypeKey,
    ids: number[]
  ): Observable<boolean> {
    return this.applyActions(
      dataType,
      ids,
      (dataType, id) =>
        this.storeService.archiveObjectByPk(
          dataType,
          id,
          storeConfigurationBulkConfigState
        ),
      'Archive',
      'Has been archived successfully'
    );
  }

  public unarchive(
    dataType: StoreDataTypeKey,
    ids: number[]
  ): Observable<boolean> {
    return this.applyActions(
      dataType,
      ids,
      (dataType, id) =>
        this.storeService.updateObjectByPk(
          dataType,
          id,
          { archivedAt: null },
          {
            updateStoreWithNetwork: false,
            updateWithOptimistic: false,
            addHistory: true,
            addToNotifications: true,
          }
        ),
      'Unarchive',
      'Has been unarchived successfully'
    );
  }

  public delete(
    dataType: StoreDataTypeKey,
    ids: number[]
  ): Observable<boolean> {
    return this.applyActions(
      dataType,
      ids,
      (dataType, id) =>
        this.storeService.deleteObjectByPk(
          dataType,
          id,
          storeConfigurationBulkConfigState
        ),
      'Delete',
      'Has been deleted successfully'
    );
  }

  private applyActions(
    dataType: StoreDataTypeKey,
    ids: number[],
    actionFn: (
      dataType: StoreDataTypeKey,
      id: number
    ) => Observable<ResultUpdate<any>>,
    action: string,
    successMessage: string
  ): Observable<boolean> {
    return this.getModalConfirmation(action, dataType).afterClose.pipe(
      switchMap((res) => {
        if (!res) {
          return of(false);
        }

        return this.rxjsOperatorsService
          .bulkOperator(
            ids.map((id, index) => {
              return actionFn(dataType, id);
            })
          )
          .pipe(
            tap(() => {
              this.storeService.propagateStoreOptimisticData(dataType);
              this.storeService.update$.next(dataType);
              this.message.create('success', successMessage);
            }),
            map((res) => true)
          );
      })
    );
  }

  private getModalConfirmation(action: string, dataType: StoreDataTypeKey) {
    return this.modal.confirm({
      nzTitle: `Do you want to ${action.toLowerCase()} these ${getEntitiesWording(
        dataType,
        true
      )}?`,
      nzContent: '',
      nzOnOk: () => true,
    });
  }

  public getCorrespondance<T>(
    dataType: StoreDataTypeKey,
    fieldsToCheck: (keyof T)[],
    search: string
  ) {
    if (!search?.length) {
      return of([]);
    }

    return this.storeService.store[dataType].data$.pipe(
      map((companies) => {
        const companiesWithFields: any[] = companies
          .map((company) => {
            return {
              ...company,
              stringField: fieldsToCheck
                .map((field) => company[field])
                .filter((r) => !!r)
                .join(' '),
            };
          })
          .filter((company) => company.stringField?.length);

        const results = companiesWithFields.map((company) => {
          return {
            data: company,
            similarity: stringSimilarity.compareTwoStrings(
              company.stringField?.toLowerCase(),
              search?.toLowerCase()
            ),
          };
        });

        const sortedResults = sortBy(results, 'similarity').reverse();

        return sortedResults.filter((r) => r.similarity > 0.8);
      })
    );
  }

  public getCorrespondanceAndShowDialog<T>(
    dataType: StoreDataTypeKey,
    fieldsToCheck: (keyof T)[],
    search: string
  ) {
    return this.getCorrespondance<T>(dataType, fieldsToCheck, search).pipe(
      take(1),
      switchMap((correspondance) => {
        if (correspondance.length > 0) {
          const content = correspondance
            .map((c) => {
              const data = c.data;
              const valuesSearch = fieldsToCheck
                .map((field) => data[field])
                .filter((r) => !!r)
                .join(' ');
              return `<a href="/features/${dataType}/edit/${
                data.id
              }" target="_blank">${getEntitiesWording(dataType)}: ${
                data.id
              }</a><br>${valuesSearch} (similarity: ${formatPercent(
                c.similarity
              )})`;
            })
            .join('<br/>');

          const confirmModal = this.modal.confirm({
            nzTitle: 'Are you sure to create the entity?',
            nzContent: `It seems that the entity already exists.<br>Fields use for similarity calculation: <b>name</b>.<br><br>${content}`,
            nzOnOk: () =>
              new Promise((resolve) => setTimeout(() => resolve(true))),
          });

          return confirmModal.afterClose.pipe(
            map((res) => {
              if (res === undefined) {
                return true;
              }
              return false;
            })
          );
        }

        return of(false);
      })
    );
  }
}
